/** La colección virtual de las entradas del blog (`CMS_BLOG_SOURCE` en el editor). */
const BLOG_SOURCE = '__blog__';
const OFFERS_SOURCE = '__offers__';
/** Donde vive el índice del blog de un proyecto nuevo (`DEFAULT_BLOG_SETTINGS.basePath`). */
const BLOG_INDEX_PATH = '/blog';
export const SEEDED_OFFER_KINDS = ['services', 'digital', 'products', 'bookings'];
/**
 * Que las listas de ofertas de la sección enseñen sólo una familia.
 *
 * Sin esto, un bloque «todas las ofertas» puesto para las reservas enseñaba
 * también los servicios, que ya tenían su propia sección: salían dos veces.
 *
 * Fuera de las reservas, lo que en la tarjeta lee la duración pasa a leer el
 * tipo («Servicio», «Producto digital»): un servicio no dura nada y el
 * antetítulo salía vacío.
 */
export function targetOffersKind(nodes, kind) {
    const retype = (node) => {
        const binding = node.contentBinding;
        if (binding?.field === 'duration')
            node.contentBinding = { ...binding, field: 'type' };
        node.children.forEach(retype);
    };
    const visit = (node) => {
        const repeat = node.cmsRepeat;
        if (repeat && (repeat.collection === OFFERS_SOURCE || repeat.offers)) {
            node.cmsRepeat = { ...repeat, offers: { ...(repeat.offers ?? {}), kind } };
            if (kind !== 'bookings')
                node.children.forEach(retype);
            stackOfferCardFooters(node.children);
        }
        node.children.forEach(visit);
    };
    nodes.forEach(visit);
}
/** Lo que pinta cada pieza de la tarjeta de un artículo, por su rol en el bloque. */
const ARTICLE_FIELDS = {
    'media.image': 'image',
    headline: 'title',
    title: 'title',
    body: 'excerpt',
    description: 'excerpt',
    eyebrow: 'categories',
    caption: 'date',
};
const BLOG_SLOTS = {
    image: 'blog.cover',
    title: 'blog.title',
    excerpt: 'blog.excerpt',
    categories: 'blog.categories',
    date: 'blog.date',
};
const baseSlot = (node) => (typeof node.aiSlot === 'string' ? node.aiSlot.trim().replace(/\.\d+$/, '') : '');
const READ_MORE = { es: 'Leer más', en: 'Read more' };
/**
 * Una sección de artículos escritos a mano, convertida en la lista del blog.
 *
 * Se queda la primera tarjeta de su lista (`repeat.list`) como modelo, y sus
 * piezas pasan a leer la entrada —portada, título, resumen, categoría, fecha—;
 * la lista repite las entradas del proyecto, tantas como tarjetas traía el
 * diseño. El título y un «Leer más» llevan a la página de la entrada. Devuelve
 * si encontró una lista que convertir: un bloque sin ella se queda como estaba.
 *
 * Va ANTES de repartir los textos (`applySectionCopy`): el reparto quita los
 * textos de muestra de una lista sin elementos, y después de él la tarjeta ya
 * sólo tenía la foto.
 */
export function bindArticlesToBlog(nodes, newId, language = 'en') {
    let list = null;
    const find = (node) => {
        if (list)
            return;
        if (baseSlot(node) === 'repeat.list' && node.children.length) {
            list = node;
            return;
        }
        node.children.forEach(find);
    };
    nodes.forEach(find);
    if (!list)
        return false;
    const host = list;
    const card = host.children.find((child) => baseSlot(child) === 'repeat.item') ?? host.children[0];
    const limit = Math.max(1, Math.min(12, host.children.length));
    let linked = false;
    const bind = (node) => {
        const field = ARTICLE_FIELDS[baseSlot(node)];
        const children = node.children.map(bind);
        const id = newId();
        const next = { ...node, id, schemaId: id, children };
        if (!field)
            return next;
        next.aiSlot = BLOG_SLOTS[field];
        next.contentBinding = { source: 'projectContent', collection: '', field };
        if (node.type === 'image')
            next.src = '';
        else
            next.content = '';
        /* El título lleva a la entrada: es lo que se pulsa en una lista de
           artículos. */
        if (field === 'title' && !linked) {
            linked = true;
            next.interaction = { action: 'link', cmsLink: { source: 'projectContent', pageSlug: '/blog/[slug]', slugField: 'slug', collection: '' } };
        }
        return next;
    };
    const model = bind(card);
    delete model.aiSlot;
    const readMoreId = newId();
    model.children.push({
        id: readMoreId,
        schemaId: readMoreId,
        /* Un botón secundario y no un enlace suelto (2026-10-07, él lo pidió): se
           lee como la acción de la tarjeta, igual que en las de ofertas. */
        type: 'button',
        label: 'Read more',
        /* Del ancho de su texto y al inicio, como el resto de la tarjeta: sin esto
           la columna lo estiraba a todo el ancho. */
        styles: { desktop: { alignSelf: 'flex-start', width: 'fit-content' } },
        content: READ_MORE[language] ?? READ_MORE.en,
        themeRole: 'buttonSecondary',
        styleRefs: { button: 'button-secondary' },
        interaction: { action: 'link', cmsLink: { source: 'projectContent', pageSlug: '/blog/[slug]', slugField: 'slug', collection: '' } },
        children: [],
    });
    host.children = [model];
    delete host.aiSlot;
    host.cmsRepeat = { source: 'projectContent', collection: BLOG_SOURCE, limit, offset: 0 };
    /* «Ver todos» lleva al índice del blog, que es donde están todos. */
    const linkActions = (node) => {
        if (node === host)
            return;
        if (baseSlot(node).startsWith('action.') && (node.type === 'link' || node.type === 'button')) {
            node.interaction = { ...(node.interaction ?? {}), action: 'link', href: BLOG_INDEX_PATH };
        }
        node.children.forEach(linkActions);
    };
    nodes.forEach(linkActions);
    return true;
}
/**
 * El pie de una tarjeta de oferta: el precio arriba y el botón debajo, no lado
 * a lado (decidido con él, 2026-10-05; ver `offerCardNode` en el editor). Los
 * diseños de tienda de la biblioteca traen el pie en fila y, en columnas de
 * tres, el botón se estrujaba. Se apila cualquier caja que lleve directamente
 * el precio (`price`) y el botón (`cta`) de la oferta. Devuelve si cambió algo.
 */
export function stackOfferCardFooters(nodes) {
    let changed = false;
    const field = (node) => node.contentBinding?.field;
    const visit = (node) => {
        const kids = node.children ?? [];
        if (kids.some((child) => field(child) === 'price') && kids.some((child) => field(child) === 'cta')) {
            const desktop = node.styles?.desktop ?? {};
            if (desktop.flexDirection !== 'column') {
                node.styles = { ...(node.styles ?? {}), desktop: { ...desktop, display: desktop.display === 'grid' ? 'flex' : desktop.display ?? 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start', gap: desktop.gap ?? '12px' } };
                changed = true;
            }
        }
        kids.forEach(visit);
    };
    nodes.forEach(visit);
    return changed;
}
//# sourceMappingURL=seededSections.js.map